import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import axios from "axios";
import { z } from "zod";
import { useAuth } from "@clerk/clerk-react";
import { trackEvent } from "@/lib/posthog";

const quizAnswerSchema = z.object({
    id: z.string(),
    answer: z.string(),
    correct: z.boolean(),
    quizPageId: z.string(),
    answerNumber: z.number(),
});

const quizPageSchema = z.object({
    id: z.string(),
    question: z.string(),
    answers: z.array(quizAnswerSchema),
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

const textPageSchema = z.object({
    id: z.string(),
    content: z.string(),
    image: z.string().optional(),
    pageId: z.string(),
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

const videoPageSchema = z.object({
    id: z.string(),
    file: z.string(),
    title: z.string(),
    content: z.string(),
    pageId: z.string(),
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

const embedPageSchema = z.object({
    id: z.string(),
    url: z.string(),
    title: z.string(),
    content: z.string(),
    pageId: z.string(),
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

const lessonPageSchema = z.object({
    id: z.string(),
    type: z.string(),
    pageNumber: z.number(),
    lessonId: z.string(),
    textPage: textPageSchema.nullable().optional(),
    quizPage: quizPageSchema.nullable().optional(),
    videoPage: videoPageSchema.nullable().optional(),
    embedPage: embedPageSchema.nullable().optional(),
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

const lessonProgressSchema = z.object({
    id: z.string(),
    currentPageId: z.string().nullable().optional(),
    completed: z.boolean(),
    lessonId: z.string(),
    userId: z.string(),
});

// Schema for lesson progress API response
const lessonProgressUpdateSchema = z.object({
    id: z.string(),
    lessonId: z.string(),
    userId: z.string(),
    completed: z.boolean(),
    currentPageId: z.string().nullable(),
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

export const lessonSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    thumbnail: z.string(),
    banner: z.string(),
    duration: z.number(),
    pages: z.array(lessonPageSchema).optional().default([]),
    lessonProgress: z.array(lessonProgressSchema).optional().default([]),
    archived: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
    exp: z.number().optional().nullable(),
});

export type Lesson = z.infer<typeof lessonSchema>;
export type LessonProgressUpdate = z.infer<typeof lessonProgressUpdateSchema>;

export const getLessonById = (lessonId: string) => {
    const { getToken } = useAuth();
    const query = useQuery({
        queryKey: ["lesson", lessonId],
        queryFn: async () => {
            const token = await getToken();
            let response;
            try {
                response = await axios.get(`${import.meta.env.VITE_API_URL}/app/lessons/${lessonId}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.status === 403) {
                    return { __forbidden: true } as any;
                }
                throw error;
            }
            return lessonSchema.parse(response.data);
        },
        enabled: !!lessonId,
    });

    // Pair with lesson_completed so completion rate and mid-lesson drop-off are
    // computable. Fires once when a real (non-forbidden) lesson opens.
    const startedId = useRef<string | null>(null);
    useEffect(() => {
        const data = query.data as any;
        if (data?.id && !data.__forbidden && startedId.current !== data.id) {
            startedId.current = data.id;
            trackEvent("lesson_started", { lesson_id: data.id });
        }
    }, [(query.data as any)?.id]);

    return query;
};

export const getAllLessons = () => {
    const { getToken } = useAuth();
    return useQuery({
        queryKey: ["lessons"],
        queryFn: async () => {
            const token = await getToken();
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/lessons`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return lessonSchema.array().parse(response.data);
        },
    });
};

export const updateLessonProgress = () => {
    const queryClient = useQueryClient();
    const { getToken } = useAuth();

    return useMutation({
        mutationFn: async ({
            lessonId,
            completed,
            currentPageId,
        }: {
            lessonId: string;
            completed?: boolean;
            currentPageId?: string;
        }) => {
            const token = await getToken();
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/app/lessons/${lessonId}/progress`,
                {
                    completed,
                    currentPageId,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return lessonProgressUpdateSchema.parse(response.data);
        },
        onSuccess: (_, variables) => {
            if (variables.completed) {
                import('@/lib/posthog').then(({ trackEvent }) => {
                    trackEvent('lesson_completed', { lesson_id: variables.lessonId });
                });
            }
            // Invalidate lesson queries to refresh progress
            queryClient.invalidateQueries({ queryKey: ["lesson", variables.lessonId] });
            queryClient.invalidateQueries({ queryKey: ["lessons"] });

            // Invalidate course queries since lesson progress affects course progress
            queryClient.invalidateQueries({ queryKey: ["courses"] });
            queryClient.invalidateQueries({ queryKey: ["courseProgress"] });
        },
    });
};